import { Link } from "@tanstack/react-router";
import type { PlayerStats, GoalkeeperStats } from "@/api/ranking";
import { getPointsBadge } from "@/lib/utils";
import { Loader } from "@/components/general/common/loader";

const SubScore = ({ label, value }: { label: string; value: number }) => (
    <div className="flex flex-col items-center gap-0.5 w-9">
        <span className="text-[9px] uppercase font-bold text-on-surface-variant tracking-wider">{label}</span>
        {value > 0 ? (
            <span className="w-full text-center text-xs tabular-nums text-on-surface font-medium leading-none">
                {value}
            </span>
        ) : (
            <span className="text-xs text-on-surface-variant leading-none">–</span>
        )}
    </div>
);

const TotalBadge = ({ value }: { value: number }) => {
    if (value <= 0) return <span className="text-on-surface-variant text-sm">–</span>;
    const { bg, text, border } = getPointsBadge(value);
    return (
        <div
            className={`w-full text-center tabular-nums py-1 rounded font-bold text-sm border ${bg} ${text} ${border}`}
        >
            {value}
        </div>
    );
};

const EmptyState = ({ message }: { message: string }) => (
    <div className="flex items-center justify-center min-h-[120px] bg-surface-container-lowest rounded-xl">
        <span className="text-on-surface-variant text-sm">{message}</span>
    </div>
);

const LoadingState = () => (
    <div className="flex items-center justify-center min-h-[120px] bg-surface-container-lowest rounded-xl">
        <Loader message="Loading..." />
    </div>
);

interface PlayerStatsMobileCardsProps {
    data: PlayerStats[];
    isLoading?: boolean;
}

export const PlayerStatsMobileCards = ({ data, isLoading = false }: PlayerStatsMobileCardsProps) => {
    if (isLoading) return <LoadingState />;
    if (data.length === 0) return <EmptyState message="No player data available" />;

    const sorted = [...data].sort((a, b) => b.totalPoints - a.totalPoints);

    return (
        <div className="flex flex-col bg-surface-container-lowest rounded-xl overflow-hidden">
            {sorted.map((player, index) => {
                const rank = index + 1;
                const rowBg = index % 2 === 0 ? "bg-surface-container-lowest" : "bg-surface-container-low";
                return (
                    <div key={player.playerId} className={`flex items-center gap-3 pl-0 pr-3 py-3 ${rowBg}`}>
                        <span
                            className={`font-headline font-bold text-sm w-9 text-center shrink-0 tabular-nums ${
                                rank === 1 ? "text-[#ba0a06]" : "text-on-surface-variant"
                            }`}
                        >
                            #{rank.toString().padStart(2, "0")}
                        </span>

                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 mb-1.5">
                                <Link
                                    to="/player/$playerId"
                                    params={{ playerId: player.playerId.toString() }}
                                    className="font-bold text-sm text-on-surface hover:text-primary truncate"
                                >
                                    {player.firstName} {player.lastName}
                                </Link>
                                <span className="px-1.5 py-0.5 bg-surface-container-highest rounded text-[9px] font-bold uppercase text-on-surface-variant shrink-0">
                                    {player.role}
                                </span>
                            </div>
                            <div className="flex items-end gap-2">
                                <SubScore label="Def" value={player.pointsDefensive} />
                                <SubScore label="Off" value={player.pointsOffensive} />
                                <SubScore label="TP" value={player.pointsTeamPlay} />
                                <SubScore label="Eff" value={player.pointsEffort} />
                                <SubScore label="Bon" value={player.pointsBonus} />
                            </div>
                        </div>

                        <div className="shrink-0 w-12 text-center">
                            <TotalBadge value={player.totalPoints} />
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

interface GoalkeeperStatsMobileCardsProps {
    data: GoalkeeperStats[];
    isLoading?: boolean;
}

export const GoalkeeperStatsMobileCards = ({ data, isLoading = false }: GoalkeeperStatsMobileCardsProps) => {
    if (isLoading) return <LoadingState />;
    if (data.length === 0) return <EmptyState message="No goalkeeper data available" />;

    const sorted = [...data].sort((a, b) => b.totalPoints - a.totalPoints);

    return (
        <div className="flex flex-col bg-surface-container-lowest rounded-xl overflow-hidden">
            {sorted.map((gk, index) => {
                const rank = index + 1;
                const rowBg = index % 2 === 0 ? "bg-surface-container-lowest" : "bg-surface-container-low";
                return (
                    <div key={gk.playerId} className={`flex items-center gap-3 pl-0 pr-3 py-3 ${rowBg}`}>
                        <span
                            className={`font-headline font-bold text-sm w-9 text-center shrink-0 tabular-nums ${
                                rank === 1 ? "text-[#ba0a06]" : "text-on-surface-variant"
                            }`}
                        >
                            #{rank.toString().padStart(2, "0")}
                        </span>

                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 mb-1.5">
                                <Link
                                    to="/player/$playerId"
                                    params={{ playerId: gk.playerId.toString() }}
                                    className="font-bold text-sm text-on-surface hover:text-primary truncate"
                                >
                                    {gk.firstName} {gk.lastName}
                                </Link>
                                <span className="px-1.5 py-0.5 bg-surface-container-highest rounded text-[9px] font-bold uppercase text-on-surface-variant shrink-0">
                                    {gk.role}
                                </span>
                            </div>
                            <div className="flex items-end gap-2">
                                <SubScore label="GSAx" value={gk.pointsGSAx} />
                                <SubScore label="HD" value={gk.pointsHighDangerShots} />
                                <SubScore label="LD" value={gk.pointsLowDangerShots} />
                                <SubScore label="LP" value={gk.pointsLateralPassShots} />
                                <SubScore label="Scr" value={gk.pointsScreenedShots} />
                            </div>
                        </div>

                        <div className="shrink-0 w-12 text-center">
                            <TotalBadge value={gk.totalPoints} />
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export interface StatRow {
    playerId: number;
    displayName: string;
    role: string | null;
    subScores: { label: string; value: number }[];
    totalPoints: number;
    teamLogoUrl?: string;
}

interface StatsMobileCardsProps {
    data: StatRow[];
    isLoading?: boolean;
}

export const StatsMobileCards = ({ data, isLoading = false }: StatsMobileCardsProps) => {
    if (isLoading) return <LoadingState />;
    if (data.length === 0) return <EmptyState message="No data available" />;

    const sorted = [...data].sort((a, b) => b.totalPoints - a.totalPoints);

    return (
        <div className="flex flex-col bg-surface-container-lowest rounded-xl overflow-hidden">
            {sorted.map((row, index) => {
                const rank = index + 1;
                const rowBg = index % 2 === 0 ? "bg-surface-container-lowest" : "bg-surface-container-low";
                return (
                    <div key={row.playerId} className={`flex items-center gap-3 pl-0 pr-3 py-3 ${rowBg}`}>
                        <div
                            className={`w-9 shrink-0 flex flex-col items-center gap-0.5 tabular-nums font-headline font-bold text-sm ${
                                rank === 1 ? "text-[#ba0a06]" : "text-on-surface-variant"
                            }`}
                        >
                            <span>#{rank.toString().padStart(2, "00")}</span>
                            {row.teamLogoUrl && (
                                <img src={row.teamLogoUrl} alt="" className="h-5 w-auto object-contain opacity-80" />
                            )}
                        </div>

                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 mb-1.5">
                                <Link
                                    to="/player/$playerId"
                                    params={{ playerId: row.playerId.toString() }}
                                    className="font-bold text-sm text-on-surface hover:text-primary truncate"
                                >
                                    {row.displayName}
                                </Link>
                                {row.role && (
                                    <span className="px-1.5 py-0.5 bg-surface-container-highest rounded text-[9px] font-bold uppercase text-on-surface-variant shrink-0">
                                        {row.role}
                                    </span>
                                )}
                            </div>
                            <div className="flex items-end gap-2">
                                {row.subScores.map(({ label, value }) => (
                                    <SubScore key={label} label={label} value={value} />
                                ))}
                            </div>
                        </div>

                        <div className="shrink-0 w-12 text-center">
                            <TotalBadge value={row.totalPoints} />
                        </div>
                    </div>
                );
            })}
        </div>
    );
};
